/**
 * Project editor page for the SaaS SPA.
 *
 * Renders the full Cascade <Editor /> with all panels (viewport, timeline,
 * sidebar, playback controls, footer) by constructing a @cascade/core Project
 * from Supabase data.
 */

import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import type {AudioClipConfig, AudioTrackConfig} from '@cascade/core';
import {useAuth} from '../auth';
import * as db from '../db';
import type {Scene} from '../db';
import {PageLoader} from '../components';
import {
  createSaaSProject,
  compileScenes,
  updatePlayerScenes,
  toRemotionScenes,
  type CompiledScene,
  type SaaSProjectBundle,
} from '../project-bridge';
import {SaaSEditorProvider} from '../contexts/editor-context';
import {Editor} from '../../Editor';
import {
  ApplicationProvider,
  PanelsProvider,
  ShortcutsProvider,
} from '../../contexts';

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export function ProjectEditor({projectId}: {projectId: string}) {
  const {supabase} = useAuth();
  const [project, setProject] = useState<db.Project | null>(null);
  const [scenes, setScenes] = useState<Scene[]>([]);
  const [compiled, setCompiled] = useState<CompiledScene[]>([]);
  const [bundle, setBundle] = useState<SaaSProjectBundle | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [audioAnalyses, setAudioAnalyses] = useState<Record<string, unknown>>({});
  const bundleRef = useRef<SaaSProjectBundle | null>(null);

  const fps = (project?.settings?.fps as number) ?? 30;
  const width = (project?.settings?.width as number) ?? 1920;
  const height = (project?.settings?.height as number) ?? 1080;

  // ── Load project + scenes from Supabase ─────────────────────────────
  const loadProject = useCallback(async () => {
    try {
      const [p, s] = await Promise.all([
        db.getProject(supabase, projectId),
        db.listScenes(supabase, projectId),
      ]);
      setProject(p);
      setScenes(s);
      setAudioAnalyses(db.getAudioAnalyses(p.settings ?? {}));
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load project');
    } finally {
      setLoading(false);
    }
  }, [supabase, projectId]);

  useEffect(() => {
    loadProject();
  }, [loadProject]);

  // ── Compile scenes when they change ─────────────────────────────────
  useEffect(() => {
    if (scenes.length === 0) {
      setCompiled([]);
      return;
    }

    let cancelled = false;

    compileScenes(scenes).then(results => {
      if (!cancelled) {
        for (const r of results) {
          if (r.error) {
            console.error(`[Cascade] Scene "${r.name}" compile error:`, r.error);
            console.error(`[Cascade] Code (first 300):`, r.code?.substring(0, 300));
          } else {
            console.log(`[Cascade] Scene "${r.name}" compiled OK, component:`, !!r.component);
          }
        }
        setCompiled(results);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [scenes]);

  // ── Debounced audio state persistence ──────────────────────────────
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const projectSettingsRef = useRef<Record<string, unknown>>({});

  // Keep settings ref in sync
  useEffect(() => {
    if (project) projectSettingsRef.current = project.settings ?? {};
  }, [project]);

  const handlePublishAudioClips = useCallback(
    (clips: AudioClipConfig[], tracks: AudioTrackConfig[]) => {
      // Debounce saves to avoid hammering Supabase on every tiny change
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(() => {
        db.saveAudioState(
          supabase,
          projectId,
          clips,
          tracks,
          projectSettingsRef.current,
        ).catch(err => console.error('[Cascade] Audio save failed:', err));
      }, 500);
    },
    [supabase, projectId],
  );

  // ── Create or update the project bundle ─────────────────────────────
  useEffect(() => {
    if (!project) return;
    // Wait for initial compilation to finish before creating the bundle.
    // compiled will be [] initially, then populated after compileScenes resolves.
    // If there are scenes but compiled is empty, compilation is still in progress.
    if (scenes.length > 0 && compiled.length === 0) return;

    if (!bundleRef.current) {
      // Load initial audio state from project settings
      const initialAudioClips =
        (project.settings?.audioClips as AudioClipConfig[]) ?? [];
      const initialAudioTracks =
        (project.settings?.audioTracks as AudioTrackConfig[]) ?? [];

      // First time: create the full bundle
      const b = createSaaSProject({
        name: project.name,
        compiledScenes: compiled,
        fps,
        width,
        height,
        initialAudioClips,
        initialAudioTracks,
        onPublishAudioClips: handlePublishAudioClips,
      });
      bundleRef.current = b;
      setBundle(b);

      // Set document title
      document.title = `${project.name} | Cascade`;
    } else {
      // Scenes changed: update player and project remotionScenes
      updatePlayerScenes(bundleRef.current.player, compiled, fps);
      // Also update the project's remotionScenes so the Remotion Player re-renders
      bundleRef.current.project.remotionScenes = toRemotionScenes(compiled, fps);
      // Force a re-render by creating a new bundle reference
      setBundle({...bundleRef.current});
    }
  }, [project, compiled, fps, width, height]);

  // ── Callback when AI creates a new scene ────────────────────────────
  const handleSceneCreated = useCallback(() => {
    loadProject();
  }, [loadProject]);

  // ── Callback when audio analysis is saved ────────────────────────
  const handleAudioAnalysisUpdated = useCallback(
    (audioSrc: string, analysis: unknown) => {
      setAudioAnalyses(prev => ({...prev, [audioSrc]: analysis}));
    },
    [],
  );

  // ── Callback when a scene is removed ──────────────────────────────
  const handleSceneRemoved = useCallback(
    async (sceneIndex: number): Promise<boolean> => {
      const scene = scenes[sceneIndex];
      if (!scene) return false;
      try {
        await db.deleteScene(supabase, scene.id);
        await loadProject();
        return true;
      } catch (err) {
        console.error('[Cascade] Failed to delete scene:', err);
        return false;
      }
    },
    [supabase, scenes, loadProject],
  );

  // ── Loading / error states ──────────────────────────────────────────
  if (loading) {
    return <PageLoader text="Loading project..." />;
  }

  if (error || !project) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          gap: 12,
          background: 'var(--background-color, #0a0a0a)',
        }}
      >
        <p style={{color: '#fca5a5', fontSize: 14}}>
          {error ?? 'Project not found'}
        </p>
        <a
          href="#/"
          style={{
            color: 'var(--theme)',
            textDecoration: 'none',
            fontSize: 13,
          }}
        >
          Back to dashboard
        </a>
      </div>
    );
  }

  if (!bundle) {
    return <PageLoader text="Initializing editor..." />;
  }

  // ── Render the full editor ──────────────────────────────────────────
  return (
    <SaaSEditorProvider
      value={{
        supabase,
        projectId,
        scenes,
        width,
        height,
        fps,
        audioAnalyses,
        audioClips: (project.settings?.audioClips as AudioClipConfig[]) ?? [],
        onSceneCreated: handleSceneCreated,
        onSceneRemoved: handleSceneRemoved,
        onAudioAnalysisUpdated: handleAudioAnalysisUpdated,
      }}
    >
      <ApplicationProvider
        application={{
          project: bundle.project,
          player: bundle.player,
          renderer: bundle.renderer as any,
          presenter: bundle.presenter as any,
          meta: bundle.meta,
          settings: bundle.settings,
          plugins: [],
          remotionScenes: bundle.project.remotionScenes,
        }}
      >
        <PanelsProvider>
          <ShortcutsProvider>
            <Editor />
          </ShortcutsProvider>
        </PanelsProvider>
      </ApplicationProvider>
    </SaaSEditorProvider>
  );
}
